import type { MouseEventHandler } from "react";
import { useEffect, useRef, useState } from "react";
import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useBuilderStore } from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";
import { useToast } from "@circle-react-shared/uikit/ToastV2";
import { LayersTreeIcon } from "../LayersTreeIcon";
import { THREE_INDENTATION_WIDTH } from "../constants";

export interface LayersTreeBlockNameProps {
  blockId: string;
  depth: number;
  isBeingEdited?: boolean;
  isLayerNameEditable: boolean;
  name: string;
  type: string;
}

export const LayersTreeBlockName = ({
  blockId,
  isLayerNameEditable,
  isBeingEdited: isBeingEditedProp = false,
  type,
  depth,
  name,
}: LayersTreeBlockNameProps) => {
  const { renameBlock } = useBuilderStore(state => ({
    renameBlock: state.renameBlock,
  }));
  const { error } = useToast();
  const [isBeingEdited, setIsBeingEdited] = useState(isBeingEditedProp);
  const inputRef = useRef<HTMLInputElement>(null);
  const blockName =
    name ||
    t(["builder_ui.block_type", type], {
      defaultValue: type,
    });

  const onBlurHandler = () => {
    setIsBeingEdited(false);
    const newName = inputRef.current?.value.trim();
    if (!newName) {
      error(t("builder_ui.layers.errors.empty_layer_name"));
      return;
    }

    if (newName === blockName) {
      return;
    }

    renameBlock(newName, blockId);
  };

  const onDoubleClickHandler: MouseEventHandler = e => {
    e.stopPropagation();
    setIsBeingEdited(true);
  };

  useEffect(() => {
    if (isBeingEdited && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isBeingEdited]);

  useEffect(() => {
    setIsBeingEdited(isBeingEditedProp);
  }, [isBeingEditedProp]);

  return (
    <div className="flex min-w-0 items-center gap-2 py-2">
      {Array.from({ length: depth }).map((_, depthIndex) => (
        <span
          aria-hidden="true"
          // eslint-disable-next-line react/no-array-index-key -- depth is stable
          key={`depth-${depth}-level-${depthIndex}`}
          className={classNames(
            "absolute top-0 flex h-full opacity-0 transition-colors before:absolute before:left-0 before:top-0 before:flex before:h-full before:border-l before:content-[''] group-hover/tree:opacity-100",
          )}
          style={{ left: `-${depthIndex * THREE_INDENTATION_WIDTH}px` }}
        />
      ))}
      <LayersTreeIcon type={type} />
      {!isBeingEdited ? (
        <span
          {...(isLayerNameEditable && { onDoubleClick: onDoubleClickHandler })}
          className={classNames("truncate capitalize", {
            "cursor-pointer": isLayerNameEditable,
          })}
        >
          {blockName}
        </span>
      ) : (
        <input
          ref={inputRef}
          type="text"
          id={blockId}
          maxLength={50}
          defaultValue={blockName}
          className="hover:border-dark focus-visible:!outline-secondary min-w-0 truncate rounded-sm border-none bg-transparent p-0 text-sm capitalize focus:ring-0"
          onBlur={onBlurHandler}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            }
          }}
        />
      )}
    </div>
  );
};
